import React from 'react';
import { Alert, Button, Card, Skeleton, Tag } from 'antd';
import { FiExternalLink } from 'react-icons/fi';
import dayjs from 'dayjs';

const normalizeDocumentUrl = (url) => {
  if (!url) return '';
  const duplicatedHostMatch = String(url).match(/^(https?:\/\/[^/]+)\/\1(\/.*)$/);
  if (duplicatedHostMatch) {
    return `${duplicatedHostMatch[1]}${duplicatedHostMatch[2]}`;
  }
  return String(url);
};

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

function DocumentLink({ label, document }) {
  if (!document?.document_url) return null;
  const href = normalizeDocumentUrl(document.document_url);
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {document.status ? (
          <Tag className="m-0">{document.status}</Tag>
        ) : null}
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline"
        >
          <FiExternalLink size={14} />
          View document
        </a>
      </div>
    </div>
  );
}

export default function BankMismatchReviewPanel({
  reviewData,
  loading = false,
  error = '',
  actionLoading = false,
  canAct = false,
  onVerify,
}) {
  if (loading) {
    return (
      <Card bordered={false} className="rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Alert type="warning" showIcon message={error} className="rounded-xl" />
    );
  }

  if (!reviewData?.has_pending_review || !reviewData?.pending_review) {
    return null;
  }

  const pending = reviewData.pending_review;
  const mismatch = pending.mismatch || {};
  const bankAccount = pending.bank_account || {};
  const chequeDocument = pending.cheque_document || {};
  const supportingDocuments = Array.isArray(pending.supporting_documents)
    ? pending.supporting_documents
    : [];
  const documentId = chequeDocument.document_id;
  const canSubmitAction = canAct && Boolean(documentId) && !actionLoading;

  return (
    <Card
      bordered={false}
      className="rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm"
      title={
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-amber-900">Pending Bank Mismatch Review</span>
          {reviewData.mismatch_status ? (
            <Tag color="orange" className="m-0">
              {reviewData.mismatch_status}
            </Tag>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-amber-900">
          Manual bank addition submitted after PAN/bank name mismatch. Review the account details and documents below.
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-amber-100 bg-white px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">PAN name</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">{safeValue(mismatch.pan_name)}</p>
          </div>
          <div className="rounded-lg border border-amber-100 bg-white px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Bank name</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">{safeValue(mismatch.bank_name)}</p>
          </div>
        </div>

        {mismatch.match_score != null ? (
          <p className="text-xs text-amber-800">
            Name match score:{' '}
            <span className="font-semibold">{Math.round(Number(mismatch.match_score))}</span>
          </p>
        ) : null}

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Submitted bank account</p>
          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            <p><span className="text-gray-500">Account Number:</span> {safeValue(bankAccount.account_number)}</p>
            <p><span className="text-gray-500">IFSC:</span> {safeValue(bankAccount.ifsc_code)}</p>
            <p><span className="text-gray-500">Bank:</span> {safeValue(bankAccount.bank_name)}</p>
            <p><span className="text-gray-500">Branch:</span> {safeValue(bankAccount.branch_name)}</p>
            <p><span className="text-gray-500">Beneficiary:</span> {safeValue(bankAccount.beneficiary_name)}</p>
            <p><span className="text-gray-500">Submitted:</span> {pending.submitted_at ? dayjs(pending.submitted_at).format('DD MMM YYYY, hh:mm A') : '—'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <DocumentLink label="Cancelled cheque / supporting document" document={chequeDocument} />
          {supportingDocuments.map((doc, index) => (
            <DocumentLink
              key={doc.document_id || doc.document_url || index}
              label={`Supporting document ${supportingDocuments.length > 1 ? index + 1 : ''}`.trim()}
              document={doc}
            />
          ))}
        </div>

        {!documentId ? (
          <Alert
            type="info"
            showIcon
            message="Cheque document ID is not available yet. Accept and reject actions are disabled until the document is linked to this user."
          />
        ) : null}

        {canAct ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="primary"
              loading={actionLoading}
              disabled={!canSubmitAction}
              onClick={() => onVerify?.('VERIFIED')}
            >
              Accept
            </Button>
            <Button
              danger
              loading={actionLoading}
              disabled={!canSubmitAction}
              onClick={() => onVerify?.('REJECTED')}
            >
              Reject
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
